import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import RebookAttributionPanel from '@/components/reporting/RebookAttributionPanel';
import InfoTip from '@/components/InfoTip';
import { ReportLineChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import ReportHeatmap, { type HeatmapColumn, type HeatmapRowData } from '@/components/reporting/ReportHeatmap';
import { useFlashHighlight } from '@/hooks/useFlashHighlight';
import { aud } from '@/utils/format';
import {
  rebookRateByDay,
  rebooksByStoreDay,
  artistQuality,
  REBOOK_BASELINE,
  REBOOK_MANAGER_TARGET,
} from '@/domain/reportingEngine';

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
}

function shortDay(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', { weekday: 'narrow' });
}

function pct(n: number | null): string {
  return n === null ? '—' : `${Math.round(n * 100)}%`;
}

interface Props {
  /** The selected date-range window for the rate line + rebook heatmap. */
  days: readonly string[];
  /** Studio scope, or undefined for all studios. */
  studioId?: StudioId;
}

/**
 * Rebooks & artists tab (Prompt F, Stage 4). Rebook-rate line against the 50%
 * baseline and 40% manager target, a stores × days rebook heatmap, the per-artist
 * quality table (ranked by estimated-minus-completed open sessions), and the
 * RebookAttributionPanel breakdown, scoped to the page-wide studio + date filters.
 */
export default function RebooksTab({ days, studioId }: Props) {
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);

  const flashing = useFlashHighlight(['booking:checked-out']);

  const rateTrend = useMemo(
    () =>
      rebookRateByDay(days, studioId)
        .filter((d) => d.cumulativePct !== null)
        .map((d) => ({ day: dayLabel(d.dayIso), Rate: Math.round(d.cumulativePct!) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings],
  );

  const storeRows = useMemo(
    () => rebooksByStoreDay(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings, studios],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quality = useMemo(() => artistQuality(studioId), [studioId, bookings, salePayments, pieces, artists]);

  const heatColumns: HeatmapColumn[] = days.map((d) => ({ key: d, label: shortDay(d), sub: d.slice(8) }));
  const heatRows: HeatmapRowData[] = storeRows
    .filter((r) => r.perDay.some((d) => d.count > 0))
    .map((r) => ({
      label: r.name,
      cells: r.perDay.map((d) => ({ colKey: d.dayIso, value: d.count, title: `${r.name} · ${dayLabel(d.dayIso)}: ${d.count} rebooks` })),
    }));

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-rebooks">
      {/* Rebook-rate line vs baselines */}
      <section className={clsx('panel p-4', flashing && 'report-flash')} data-testid="rebooks-rate-line">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Rebook rate
          <InfoTip>
            Running rebook rate across the week: cumulative in-chair rebooks ÷ cumulative completed sessions, as a
            percentage. Reference lines mark the 50% studio baseline and the 40% manager target.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Running rate this week vs the 50% baseline and 40% manager target
        </p>
        {rateTrend.length > 0 ? (
          <ReportLineChart
            data={rateTrend}
            xKey="day"
            hideLegend
            series={[{ key: 'Rate', name: 'Rebook rate', color: REPORT_COLORS.mint }]}
            references={[
              { y: REBOOK_BASELINE * 100, label: '50% baseline', color: REPORT_COLORS.gold },
              { y: REBOOK_MANAGER_TARGET * 100, label: '40% target', color: REPORT_COLORS.azure },
            ]}
          />
        ) : (
          <p className="font-serif text-cream-soft text-sm">No completed sessions this week yet.</p>
        )}
      </section>

      {/* Rebook heatmap: stores × days */}
      <section className="panel p-4" data-testid="rebooks-heatmap">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Rebooks by store
          <InfoTip>
            In-chair rebooks captured per store per day this week. Darker cells = more rebooks that day. Stores with
            no rebooks this week are hidden.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          In-chair rebooks per store per day this week
        </p>
        <ReportHeatmap
          columns={heatColumns}
          rows={heatRows}
          tone="mint"
          caption="Darker = more rebooks captured that day"
          emptyLabel="No rebooks captured this week yet."
        />
      </section>

      {/* Per-artist quality mini-table */}
      <section className="panel p-4" data-testid="rebooks-quality-table">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Artist quality
          <InfoTip>
            Per artist (all-time): average job value, repeat-booking rate (customers who came back for &gt;1 session),
            piece-completion rate (completed ÷ terminal pieces), and open sessions left (estimated − completed across
            open pieces). Ranked by open sessions left — the artist whose booked work is dragging sits on top.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Ranked by open sessions still to run (estimated − completed) — booked work that is dragging
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Artist</th>
                <th className="text-right px-2 py-2">Avg job</th>
                <th className="text-right px-2 py-2">Repeat rate</th>
                <th className="text-right px-2 py-2">Piece completion</th>
                <th className="text-right px-2 py-2">Open sessions left</th>
              </tr>
            </thead>
            <tbody>
              {quality.map((r) => (
                <tr key={r.artistId} className="border-t border-gold-dim/15" data-testid={`rebooks-quality-${r.artistId}`}>
                  <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{r.name}</td>
                  <td className="px-2 py-2 text-right font-mono text-gold-bright">
                    {r.avgJobValueAud > 0 ? aud(r.avgJobValueAud) : '—'}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-cream-soft">{pct(r.repeatRate)}</td>
                  <td className="px-2 py-2 text-right font-mono text-cream-soft">{pct(r.pieceCompletionRate)}</td>
                  <td
                    className={clsx('px-2 py-2 text-right font-mono', r.openSessionsRemaining > 0 ? 'text-blood' : 'text-mint')}
                    data-testid={`rebooks-open-${r.artistId}`}
                  >
                    {r.openSessionsRemaining}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Attribution panel, now scoped to the page-wide studio + date filters */}
      <RebookAttributionPanel days={days} studioId={studioId} />
    </div>
  );
}
